import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { User } from '@/types'
import * as authApi from '../../services/api/auth'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

const getInitialState = (): AuthState => {
  const token = localStorage.getItem('auth_token')
  const userStr = localStorage.getItem('user')
  let user = null
  
  try {
    if (userStr) {
      user = JSON.parse(userStr)
    }
  } catch (error) {
    console.error('解析使用者資料失敗:', error)
    localStorage.removeItem('user')
  }
  
  return {
    user,
    isAuthenticated: !!token && !!user,
    loading: false,
    error: null,
  }
}

const initialState: AuthState = getInitialState()

export const loginWithEmail = createAsyncThunk(
  'auth/loginWithEmail',
  async (credentials: { email: string; password: string }) => {
    const response = await authApi.loginWithEmail(credentials)
    return response.data
  }
)

export const loginWithGoogle = createAsyncThunk(
  'auth/loginWithGoogle',
  async (code: string) => {
    const response = await authApi.loginWithGoogle(code)
    return response.data
  }
)

export const loginWithLine = createAsyncThunk(
  'auth/loginWithLine',
  async (params: { code: string }) => {
    const response = await authApi.loginWithLine(params.code)
    return response.data
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    await authApi.logout()
  }
)

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async () => {
    console.log('getCurrentUser called')
    try {
      const response = await authApi.getCurrentUser()
      console.log('getCurrentUser response:', response)
      return response.data
    } catch (error) {
      console.error('getCurrentUser error:', error)
      throw error
    }
  }
)

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (updateData: { 
    name?: string
    email?: string
    currentPassword?: string
    newPassword?: string
  }) => {
    const response = await authApi.updateProfile(updateData)
    return response.data
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Login with Email
      .addCase(loginWithEmail.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginWithEmail.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload?.user || null
        if (action.payload?.token) {
          localStorage.setItem('auth_token', action.payload.token)
          localStorage.setItem('user', JSON.stringify(action.payload.user))
        }
      })
      .addCase(loginWithEmail.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '登入失敗'
      })
      // Login with Google
      .addCase(loginWithGoogle.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload?.user || null
        if (action.payload?.token) {
          localStorage.setItem('auth_token', action.payload.token)
          localStorage.setItem('user', JSON.stringify(action.payload.user))
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '登入失敗'
      })
      // Login with LINE
      .addCase(loginWithLine.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginWithLine.fulfilled, (state, action) => {
        state.loading = false
        state.isAuthenticated = true
        state.user = action.payload?.user || null
        if (action.payload?.token) {
          localStorage.setItem('auth_token', action.payload.token)
          localStorage.setItem('user', JSON.stringify(action.payload.user))
        }
      })
      .addCase(loginWithLine.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'LINE 登入失敗'
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false
        state.user = null
        state.isAuthenticated = false
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
      })
      .addCase(logout.rejected, (state) => {
        state.loading = false
        // 即使登出 API 失敗，也清除本地認證狀態
        // 因為用戶意圖是要登出，不應該因為後端錯誤而阻止登出
        state.user = null
        state.isAuthenticated = false
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        state.error = null // 不顯示錯誤訊息
      })
      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload?.user || null
        state.isAuthenticated = true
        if (action.payload?.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user))
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        console.log('getCurrentUser rejected:', action.error.message)
        state.loading = false
        state.error = null // 清除錯誤，避免顯示給使用者
        
        // 只有在明確的認證失敗（401）時才清除認證狀態
        // 其他錯誤（如網路錯誤）不應該強制登出用戶
        if (action.error.message?.includes('401') || action.error.message?.includes('Unauthorized')) {
          state.user = null
          state.isAuthenticated = false
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user')
        }
        // 對於其他錯誤，保持當前認證狀態不變
      })
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload?.user) {
          state.user = action.payload.user
          localStorage.setItem('user', JSON.stringify(action.payload.user))
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '更新個人資料失敗'
      })
  },
})

export const { clearError } = authSlice.actions
export default authSlice.reducer